import * as React from 'react';
import { UploadIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuTrigger,
} from './action-menu';
import { MenuDotsVerticalIcon, MenuEditIcon, MenuTrashIcon } from './menu-icons';

export interface DropzoneEmptyFaceProps {
  /** Dropzone title (20px semibold; 14px medium in the `tile` variant). */
  title: React.ReactNode;
  /** Format hint under the title (14px regular). Hidden in the `tile` variant. */
  description: React.ReactNode;
  /** Brand-blue browse action label. Hidden in the `tile` variant. */
  browseLabel: React.ReactNode;
  dragging: boolean;
  disabled?: boolean;
  /** Invalid state (paints the error border). */
  invalid?: boolean;
  /** `aria-describedby` hint id for the dropzone button. */
  describedBy?: string;
  /**
   * Face geometry. Defaults to the dashed dropzone; `tile` renders the
   * compact gray logo tile (Noor-CPanel merchant dialog): a 148px
   * `rounded-[12px]` gray-100 box with a 40px icon disc and the centered
   * 14px-medium title. Behavior (dialog, drag & drop, guards) is identical.
   */
  variant?: 'default' | 'tile' | 'banner';
  onOpen: () => void;
  onDraggingChange: (dragging: boolean) => void;
  onDropFiles: (files: FileList | null) => void;
}

/**
 * Empty dashed dropzone face (Figma 732:18311) — the 68px icon disc, the 20px
 * semibold title, the format hint, and the brand-blue browse action. Shared by
 * `Dropzone` and `ImageUpload` so every image upload renders one design.
 */
export function DropzoneEmptyFace({
  title,
  description,
  browseLabel,
  dragging,
  disabled = false,
  invalid = false,
  describedBy,
  variant = 'default',
  onOpen,
  onDraggingChange,
  onDropFiles,
}: DropzoneEmptyFaceProps) {
  const dropHandlers = {
    onDragOver: (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onDraggingChange(true);
    },
    onDragLeave: () => onDraggingChange(false),
    onDrop: (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onDraggingChange(false);
      onDropFiles(e.dataTransfer.files);
    },
  };

  if (variant === 'tile' || variant === 'banner') {
    const banner = variant === 'banner';
    return (
      <button
        type="button"
        onClick={onOpen}
        {...dropHandlers}
        aria-describedby={describedBy}
        data-invalid={invalid}
        data-variant={variant}
        disabled={disabled}
        className={cn(
          'flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-[12px] bg-gray-100 transition-colors',
          banner ? 'h-[104px] p-3' : 'h-[148px] p-5',
          'outline-none disabled:cursor-not-allowed disabled:opacity-60',
          dragging && 'bg-gray-200',
          'hover:bg-gray-200/70',
          'focus-visible:ring-[3px] focus-visible:ring-ring/50',
          'data-[invalid=true]:ring-[3px] data-[invalid=true]:ring-input-border-invalid/40',
        )}
      >
        <span
          className={cn(
            'flex items-center justify-center rounded-full bg-[#e4e7ec]',
            banner ? 'size-8' : 'size-10',
          )}
        >
          <UploadIcon className="size-5 text-form-label-fg" aria-hidden="true" />
        </span>
        <span className="text-center text-sm font-medium tracking-[-0.28px] text-black">
          {title}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      {...dropHandlers}
      aria-describedby={describedBy}
      data-invalid={invalid}
      disabled={disabled}
      className={cn(
        'flex min-h-[150px] w-full cursor-pointer flex-col items-center gap-3 rounded-2xl border border-dashed border-dropzone-border bg-dropzone-bg p-5 transition-colors outline-none disabled:cursor-not-allowed disabled:opacity-60',
        dragging && 'border-primary bg-primary/5',
        'hover:border-primary',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'data-[invalid=true]:border-input-border-invalid',
      )}
    >
      <span className="flex size-[68px] items-center justify-center rounded-full bg-dropzone-icon-bg">
        <UploadIcon className="h-7 w-[29px] text-form-label-fg" aria-hidden="true" />
      </span>
      <span className="flex w-full flex-col items-center gap-3 pt-2.5">
        <span className="text-center text-xl leading-[26px] font-semibold tracking-[-0.4px] text-form-title-fg">
          {title}
        </span>
        <span className="max-w-[290px] pb-[11px] text-center text-sm font-normal tracking-[-0.28px] text-form-label-fg">
          {description}
        </span>
        <span className="text-center text-sm font-normal tracking-[-0.28px] text-link-brand-fg">
          {browseLabel}
        </span>
      </span>
    </button>
  );
}

export interface DropzoneFilledFaceProps {
  /** Preview image source (blob: URL or server URL). */
  previewSrc: string;
  disabled?: boolean;
  /** Replace-image action label. */
  editLabel: string;
  /** Delete-image action label (also the ⋮ button name). */
  removeLabel: string;
  /** Face geometry — `tile` renders the 148px compact preview, `banner` the 104px one. */
  variant?: 'default' | 'tile' | 'banner';
  onReplace: () => void;
  onRemove: () => void;
}

/**
 * Filled photo face (Figma 755:24553) — the photo on cover with a floating
 * Change / Remove menu under the ⋮ button. Shared by `Dropzone` and
 * `ImageUpload` so every image upload renders one design.
 */
export function DropzoneFilledFace({
  previewSrc,
  disabled = false,
  editLabel,
  removeLabel,
  variant = 'default',
  onReplace,
  onRemove,
}: DropzoneFilledFaceProps) {
  return (
    <div
      data-slot="dropzone-preview"
      data-variant={variant}
      className={
        variant === 'tile' || variant === 'banner'
          ? cn(
              'relative flex w-full flex-col items-end gap-1 overflow-hidden rounded-[12px] p-2',
              variant === 'banner' ? 'h-[104px]' : 'h-[148px]',
            )
          : 'relative flex h-[242px] min-h-[150px] flex-col items-end gap-1 overflow-hidden rounded-2xl border border-dashed border-dropzone-border p-5'
      }
    >
      <img
        src={previewSrc}
        alt=""
        className={cn(
          'pointer-events-none absolute inset-0 size-full object-cover',
          variant === 'default' ? 'rounded-2xl' : 'rounded-[12px]',
        )}
      />
      <ActionMenu>
        <ActionMenuTrigger asChild>
          <button
            type="button"
            aria-label={editLabel}
            disabled={disabled}
            className="relative flex shrink-0 cursor-pointer items-center justify-center rounded-[18px] bg-card p-2 text-ink-fg shadow-float-halo transition-colors outline-none hover:bg-surface-warm focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <MenuDotsVerticalIcon aria-hidden="true" className="size-5" />
          </button>
        </ActionMenuTrigger>
        <ActionMenuContent align="end" side="bottom" className="w-60">
          <ActionMenuItem onSelect={onReplace} className="min-h-11 text-sm font-medium">
            <MenuEditIcon aria-hidden="true" className="size-5" />
            {editLabel}
          </ActionMenuItem>
          <ActionMenuItem
            variant="destructive"
            onSelect={onRemove}
            className="min-h-11 text-sm font-medium"
          >
            <MenuTrashIcon aria-hidden="true" className="size-5" />
            {removeLabel}
          </ActionMenuItem>
        </ActionMenuContent>
      </ActionMenu>
    </div>
  );
}
